import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { DialogActions, DialogForm } from './DialogActions';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Field } from './Field';
import { classifyError, humanizeError } from '../lib/errors';
import { FormError } from './FormError';
import { useDiscardGuard } from '../hooks/useDiscardGuard';

const MIN_ORG_NAME_LENGTH = 2;
const MAX_ORG_NAME_LENGTH = 100;
const ORG_NAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9 .'-]*[A-Za-z0-9]$|^[A-Za-z0-9]$/;

const normalizeOrganizationName = (value: string): string => value.trim().replace(/\s+/g, ' ');

const validateOrganizationName = (value: string): string | null => {
  if (!value) {
    return 'Organization name is required.';
  }
  if (value.length < MIN_ORG_NAME_LENGTH) {
    return `Organization name must be at least ${MIN_ORG_NAME_LENGTH} characters.`;
  }
  if (value.length > MAX_ORG_NAME_LENGTH) {
    return `Organization name must be ${MAX_ORG_NAME_LENGTH} characters or fewer.`;
  }
  if (!ORG_NAME_REGEX.test(value)) {
    return "Organization name can contain letters, numbers, spaces, dots, apostrophes, and hyphens.";
  }
  return null;
};

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, licenseKey: string) => Promise<void>;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  // Local, not the parent's organization-loading flag: that one is also set by
  // unrelated refreshes, and this lock is about THIS request only.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [licenseError, setLicenseError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setOrganizationName('');
      setLicenseKey('');
      setNameError(null);
      setLicenseError(null);
      setFormError(null);
    }
  }, [isOpen]);

  const guard = useDiscardGuard({
    isDirty: Boolean(organizationName.trim() || licenseKey.trim()),
    isBusy: isSubmitting,
    onClose,
    recordNoun: 'the new organization',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Every field is checked, and every error shown at once (DESIGN_SYSTEM §5
    // Validation): Submit stays enabled so these messages can be reached.
    const normalizedName = normalizeOrganizationName(organizationName);
    const nameProblem = validateOrganizationName(normalizedName);
    const licenseProblem = licenseKey.trim() ? null : 'License key is required.';
    setNameError(nameProblem);
    setLicenseError(licenseProblem);
    if (nameProblem || licenseProblem) return;

    setIsSubmitting(true);
    try {
      await onCreate(normalizedName, licenseKey.trim());
      toast.success('Organization created');
      // Modal will be closed by parent component after successful creation
    } catch (err: unknown) {
      console.error('Failed to create organization:', err);
      // The slug is derived from the name, so a taken slug is the name's problem too.
      const failure = classifyError(err);
      if (failure.kind === 'conflict' && (failure.field === 'name' || failure.field === 'slug')) {
        setNameError('That name is already in use. Choose a different one.');
      } else {
        setFormError(humanizeError(err, "Couldn't create the organization"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // `pending` locks Escape, outside-click and the X while the create runs.
    <>
    <Dialog open={isOpen} pending={isSubmitting} onOpenChange={guard.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>Give your organization a name to continue.</DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
          <Field label="Organization name" error={nameError} required>
            {(control) => (
              <Input
                {...control}
                type="text"
                value={organizationName}
                onChange={(e) => {
                  setOrganizationName(e.target.value);
                  setNameError(null);
                  setFormError(null);
                }}
                placeholder="Enter organization name"
                disabled={isSubmitting}
                maxLength={MAX_ORG_NAME_LENGTH}
                autoComplete="off"
                autoFocus
              />
            )}
          </Field>
          <Field
            label="License key"
            error={licenseError}
            help="Issued by a Dory system administrator. A key can be limited to a number of uses or expire."
            required
          >
            {(control) => (
              // A pasted identifier the person must be able to check: a mono
              // text input, never type="password" (DESIGN_SYSTEM.md §4
              // CredentialField). Sent trimmed; the API compares it exactly.
              <Input
                {...control}
                type="text"
                mono
                value={licenseKey}
                onChange={(e) => {
                  setLicenseKey(e.target.value);
                  setLicenseError(null);
                  setFormError(null);
                }}
                placeholder="Enter license key"
                disabled={isSubmitting}
                autoComplete="off"
              />
            )}
          </Field>
          </DialogBody>
          <FormError message={formError} />
          <DialogActions>
            <Button type="button" variant="outline" onClick={guard.requestClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Create organization
            </Button>
          </DialogActions>
        </DialogForm>
      </DialogContent>
    </Dialog>
    {guard.guardDialog}
    </>
  );
};

export default CreateOrganizationModal;
